import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as React from 'react';
import cn from 'classnames';
import { xorBy, isNull } from 'lodash-es';
import { IconCaretDown } from '../../../../icons';
import { isFunction, safeInvoke, controlClasses, intentClass, keys, ControlShapeType, ControlAppearanceType, Intent, ControlDensityType } from '../../../../library';
import { Popover } from '../../../popover';
import { Close } from '../../../close';
import { OptionList, OptionItem } from './option-list';
export var selectClasses = {
  MAIN: 'select',
  INPUT: 'select__input',
  OPTIONS: 'select__options',
  LABEL: 'control__text',
  PLACEHOLDER: '-placeholder',
  ACTION: 'select__action',
  ADDON: 'control__addon'
};
export var Select = function Select(props) {
  var optionListRef = React.useRef(null);
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    targetRef = _React$useState2[0],
    setTargetRef = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isOpen = _React$useState4[0],
    setIsOpen = _React$useState4[1];
  var _React$useState5 = React.useState(0),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    width = _React$useState6[0],
    setWidth = _React$useState6[1];
  React.useEffect(function () {
    if (!!targetRef) {
      var _width = targetRef.getBoundingClientRect().width;
      setWidth(_width);
    }
  }, [targetRef]);
  var clearSelect = function clearSelect(e) {
    e.stopPropagation();
    safeInvoke(props.onClearActive);
    safeInvoke(props.onChange, []);
    safeInvoke(props.onUpdate, props.field, []);
    targetRef === null || targetRef === void 0 || targetRef.focus();
  };
  var handlePopoverVisibilityChange = function handlePopoverVisibilityChange(nextOpenState) {
    setIsOpen(nextOpenState);
  };
  var handlePopoverOpening = function handlePopoverOpening() {
    var _optionListRef$curren3;
    var activeOption;
    if (props.activeOptions && props.activeOptions.length > 0 && !props.allowMultiple) {
      activeOption = props.activeOptions[0];
    } else {
      var _optionListRef$curren, _optionListRef$curren2;
      activeOption = (_optionListRef$curren = (_optionListRef$curren2 = optionListRef.current) === null || _optionListRef$curren2 === void 0 ? void 0 : _optionListRef$curren2.getFirstEnabledOption()) !== null && _optionListRef$curren !== void 0 ? _optionListRef$curren : null;
    }
    (_optionListRef$curren3 = optionListRef.current) === null || _optionListRef$curren3 === void 0 || _optionListRef$curren3.setFocusOption(activeOption, optionListRef.current.scrollFocusedItemToTop);
  };
  var handlePopoverResize = function handlePopoverResize() {
    if (isNull(targetRef)) return;
    var width = targetRef.getBoundingClientRect().width;
    setWidth(width);
  };
  var handleTargetBlur = function handleTargetBlur(e) {
    safeInvoke(props.onBlur, e);
  };
  var handleOptionSelect = function handleOptionSelect(option, e) {
    safeInvoke(props.onOptionSelect, option, e);
    if (props.allowMultiple) {
      var activeOptions = xorBy([option], props.activeOptions, props.optionIdProp);
      safeInvoke(props.onUpdate, props.field, activeOptions);
      safeInvoke(props.onChange, activeOptions);
    } else {
      safeInvoke(props.onUpdate, props.field, [option]);
      safeInvoke(props.onChange, [option]);
    }
    if (props.closeOnSelect === true || props.closeOnSelect === 'auto' && !props.allowMultiple) {
      setIsOpen(false);
      targetRef === null || targetRef === void 0 || targetRef.focus();
    }
  };
  var handleTargetKeyDown = function handleTargetKeyDown(event) {
    var optionList = optionListRef.current;
    if (event.which === keys.ARROW_UP || event.which === keys.ARROW_DOWN || event.which === keys.SPACE) {
      setIsOpen(true);
    }
    if (event.which === keys.ESCAPE || event.which === keys.TAB) {
      setIsOpen(false);
    }
    if (!optionList) return;
    optionList.handleKeyDown(event);
  };
  function handleOptionsMouseOut(e) {
    var optionList = optionListRef.current;
    optionList === null || optionList === void 0 || optionList.setFocusOption(null);
  }
  var renderOptionDefault = function renderOptionDefault(option, itemProps) {
    if (isFunction(props.renderOption)) {
      return props.renderOption(option, itemProps);
    } else {
      return React.createElement(OptionItem, {
        key: itemProps.id,
        isActive: itemProps.modifiers.active,
        isDisabled: itemProps.modifiers.disabled,
        isFocused: itemProps.modifiers.focused,
        onClick: itemProps.handleClick,
        onMouseOver: itemProps.handleMouseEnter,
        id: itemProps.id
      }, option[props.renderOption]);
    }
  };
  var renderOptions = function renderOptions() {
    return React.createElement(OptionList, {
      id: "".concat(props.field, "__list"),
      activeOptions: props.activeOptions,
      className: cn({
        '-multiple': props.allowMultiple
      }),
      noResults: props.noResults,
      options: props.options,
      optionDisabled: props.optionDisabled,
      optionIdProp: props.optionIdProp,
      optionSearchProp: props.optionSearchProp,
      onFocusedOptionChange: props.onFocusedOptionChange,
      onOptionSelect: handleOptionSelect,
      onKeyDown: props.onKeyDown,
      onKeyUp: props.onKeyUp,
      onMouseOut: handleOptionsMouseOut,
      ref: optionListRef,
      renderOption: renderOptionDefault,
      style: {
        minWidth: width
      },
      labeledBy: "".concat(props.field, "__label")
    });
  };
  var renderLabel = function renderLabel() {
    var _props$activeOptions = props.activeOptions,
      activeOptions = _props$activeOptions === void 0 ? [] : _props$activeOptions;
    var hasActiveItem = props.activeOptions && props.activeOptions.length > 0;
    var classNames = cn(selectClasses.LABEL, {
      '-placeholder': !hasActiveItem
    });
    var label = '';
    if (hasActiveItem) {
      if (isFunction(props.renderLabel)) {
        label = props.renderLabel(activeOptions);
      } else if (activeOptions.length > 1) {
        label = _toConsumableArray(activeOptions).reverse().map(function (option, index) {
          if (isFunction(props.renderLabel)) return undefined;
          return React.createElement("span", {
            key: index
          }, option[props.renderLabel], index + 1 === activeOptions.length ? null : ',', "\xA0");
        });
      } else {
        label = activeOptions[0][props.renderLabel];
      }
    }
    return React.createElement("span", {
      className: classNames,
      id: "".concat(props.field, "__label")
    }, hasActiveItem ? label : props.placeholder);
  };
  var renderClear = function renderClear() {
    if (props.isDisabled || props.isReadOnly || !props.isClearable || !props.activeOptions || props.activeOptions.length === 0) {
      return null;
    }
    return React.createElement(Close, {
      className: selectClasses.ADDON,
      shape: props.shape,
      appearance: "fill",
      size: props.size,
      onClick: clearSelect
    });
  };
  var shape = props.shape,
    density = props.density,
    intent = props.intent,
    size = props.size,
    variant = props.appearance;
  var classNames = cn(selectClasses.MAIN, intentClass(intent), controlClasses({
    shape: shape,
    density: density,
    size: size,
    appearance: variant
  }), {
    '-required': props.isRequired,
    '-disabled': props.isDisabled,
    '-inline': props.isInline,
    '-readonly': props.isReadOnly
  }, props.className);
  return React.createElement(Popover, {
    autoFocus: false,
    enforceFocus: false,
    className: selectClasses.OPTIONS,
    content: renderOptions(),
    isOpen: isOpen,
    isClosedOnEscape: true,
    isMinimal: true,
    isDisabled: props.isDisabled || props.isReadOnly,
    onOpening: handlePopoverOpening,
    onResize: handlePopoverResize,
    onVisibilityChange: handlePopoverVisibilityChange,
    position: "bottom",
    boundary: props.boundary,
    getReferenceRef: setTargetRef,
    transitionDuration: 0,
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, 0]
      }
    }]
  }, props.children ? props.children({
    defaultLabel: renderLabel(),
    eventHandlers: {
      onBlur: handleTargetBlur,
      onKeyDown: handleTargetKeyDown
    }
  }) : React.createElement("div", {
    className: classNames,
    onBlur: handleTargetBlur,
    onKeyDown: handleTargetKeyDown,
    style: props.style,
    tabIndex: props.isDisabled ? undefined : 0,
    "aria-haspopup": "listbox",
    "aria-expanded": isOpen,
    "aria-label": props['aria-label']
  }, !!props.before ? React.createElement("div", {
    className: selectClasses.ADDON
  }, props.before) : null, renderLabel(), !props.isReadOnly && React.createElement("span", {
    className: cn(selectClasses.ADDON, selectClasses.ACTION)
  }, React.createElement(IconCaretDown, null)), renderClear()));
};
Select.defaultProps = {
  appearance: ControlAppearanceType.OUTLINE,
  boundary: 'viewport',
  closeOnSelect: 'auto',
  density: ControlDensityType.NORMAL,
  isClearable: false,
  isDisabled: false,
  intent: Intent.NONE,
  placeholder: 'Select',
  shape: ControlShapeType.ROUNDED
};